import { Component } from 'react';
import styled from 'styled-components';
import { matchPath, RouteComponentProps, withRouter } from 'react-router-dom';
import { animateScroll } from 'react-scroll';
import FetchComponent from '../../../components/behavior/FetchComponent';
import IfSocialLinksAvailable from '../../../components/behavior/IfSocialLinksAvailable';
import { FontIconProps, FontIconSpan } from '../../../components/FontIcon';
import { Spinner } from '../../../components/Spinner';
import {
  Sticky,
  StickyElement,
  StickyElementLink
} from '../../../components/StickySide';
import { scrollOptions } from '../../../configs';
import {
  withModal,
  WithModalProps
} from '../../../provider/ModalProvider/withModal';
import { selectMeta, selectSearchMenuEntry } from '../../../provider/Typo3Provider/selectors';
import Typo3Label from '../../../provider/Typo3Provider/Typo3Label';
import {
  withTypo3,
  WithTypo3Props
} from '../../../provider/Typo3Provider/withTypo3';
import { withTypo3Translation, WithTypo3TranslationProps } from "../../../provider/Typo3Provider/withTypo3Translation";
import { StyledSpanComponent } from '../../../types';
import { StickyContactFormProps } from '../../FormContainer/StickyContactForm';
import SocialLinksContainer, { SocialLinksContainerProps } from '../../SocialLinksContainer';
import TypoVideo from '../../../components/TypoVideo';
import { Typo3Files } from '../../../provider/Typo3Provider/types';
import { SvgIcon, SvgIconBefore } from '../../../components/SvgIcon';

const AsyncStickyContactForm = (props: StickyContactFormProps) => (
  <FetchComponent
    fetcher={() =>
      import('../../FormContainer/StickyContactForm' /* webpackChunkName: "sticky-contact-form", webpackPrefetch: true */)
    }
    placeholder={<Spinner />}
    props={props}
  />
);

export interface StickVideoProps {
    closeModal?: () => void;
}

const AnniversaryVideo: Typo3Files = {
    assets: [{
        uid: 0,
        identifier: 'AnniversaryVideo',
        extension: '',
        name: 'Anniversary Video',
        imageUri: '/fileadmin/user_upload/demo/Video/BusStore_Jubilaeumsvideo_10Jahre_web.mp4',
        imageZoomUri: 'BusStore_Jubilaeumsvideo_10Jahre_web.mp4',
    }],
};

const AnniversaryVideoFR: Typo3Files = {
    assets: [{
        uid: 0,
        identifier: 'AnniversaryVideoFr',
        extension: '',
        name: "Vidéo d'anniversaire",
        imageUri: '/fileadmin/user_upload/demo/Video/Jubiläumsvideo/BusStore_Jubilaeumsvideo_10Jahre_FR_web.mp4',
        imageZoomUri: 'BusStore_Jubilaeumsvideo_10Jahre_FR_web.mp4',
    }],
};

const FontIconSpanPrint: StyledSpanComponent<
  FontIconProps
> = FontIconSpan.extend`
  line-height: 32px;
  display: inline-block;

  &:before {
    font-size: 32px;
    margin-right: 16px;
    vertical-align: bottom;
  }
`;

export const AlertElementContainer = styled.div`
    position: absolute;
`;

const StickyAlertElement = StickyElement.extend`
  margin-left: 8px;
  animation: sticky-alert-slide-in-out 4s 1 ease-in-out;
  right: 0;
  position: relative;

  @keyframes sticky-alert-slide-in-out {
    from { right: 0; }
    20% { right: 100%; }
    80% { right: 100%; }
    to { right: 0; }
  }
`;

interface StickySideElementProps {}
type StickySideElementState = {
  showCopyAlertTimeout: number | undefined;
  hasScrolled: boolean;
};

class StickySideElement extends Component<
  StickySideElementProps & WithModalProps & WithTypo3TranslationProps & RouteComponentProps & WithTypo3Props & SocialLinksContainerProps,
  StickySideElementState
> {

  public state = {
    showCopyAlertTimeout: undefined,
    hasScrolled: false,
  };

  private StickyVideo = (props: StickVideoProps) =>
  this.props.typo3.pathContent?.meta.sys_language_isocode === 'fr' ? (
      <TypoVideo aspectRatioHeight={10} file={AnniversaryVideoFR} play={true} muted={true} />
  ) : (
      <TypoVideo aspectRatioHeight={8} file={AnniversaryVideo} play={true} muted={true} />
    );

  public componentDidMount(): void {
    window.addEventListener('scroll', this.onCheckScroll);
    this.onCheckScroll();
  }

  public componentWillUnmount() {
    if (this.state.showCopyAlertTimeout) {
      window.clearTimeout(this.state.showCopyAlertTimeout);
    }

    window.removeEventListener('scroll', this.onCheckScroll);
  }

  private onCheckScroll = () => {
    if (this.state.hasScrolled || document.documentElement.scrollTop < 100) {
      return;
    }

    this.setState({hasScrolled: true});
  };

  public getSearchLink() {
    const searchEntry = selectSearchMenuEntry(this.props.typo3);
    if (searchEntry) {
      return searchEntry.link;
    }
    return null;
  }

  public getExternalLink(param:string) {
    const {typo3Translate} = this.props;
    const theLink = typo3Translate(param);
    if (theLink && theLink.startsWith('http')) {
      return theLink;
    }
    return false;
  }

  public onClickToTopOfPage = () => {
    animateScroll.scrollToTop(scrollOptions);
  };

  public showAnniversaryVideo = () => {
    this.props.modalApi.showModal(<this.StickyVideo />, {plain: true, forVideo: true});
  };

  public showForm = () => {
    this.props.modalApi.showModal(<AsyncStickyContactForm />);
  };

  public onCopiedToClipboard = () => {
    if (this.state.showCopyAlertTimeout) {
      return;
    }

    const showCopyAlertTimeout = window.setTimeout(() => {
      this.setState({showCopyAlertTimeout: undefined});
    }, 5000);
    this.setState({showCopyAlertTimeout});
  };

  public render() {
    const { location } = this.props;
    const searchLink = this.getSearchLink();

    const showPrintButton = searchLink
      ? !!matchPath(location.pathname, {
          path: searchLink,
          exact: true,
          strict: true
        }) && !new URLSearchParams(location.search).has('print')
      : false;

    return (
      <Sticky className="hide-for-print" hide={!this.state.hasScrolled}>
        <StickyElement onClick={this.onClickToTopOfPage}>
          <FontIconSpan icon="arrowUp">
            <Typo3Label label="stickySide.buttonUp" />
          </FontIconSpan>
        </StickyElement>

        {/* <StickyElement primaryColor onClick={this.showAnniversaryVideo}>
          <SvgIcon IconHeight={1.5} marginRight={16} icon="play" svgStyles={{ stroke: 'currentColor' }} />
          <Typo3Label label="stickySide.anniversaryVideo" />
        </StickyElement> */}

        <StickyElement onClick={this.showForm}>
          <FontIconSpan icon="calling_outline">
            <Typo3Label label="stickySide.buttonContact" />
          </FontIconSpan>
        </StickyElement>

        <IfSocialLinksAvailable>
          <StickyElement noPointer>
            <FontIconSpan icon="share_outline">
              <Typo3Label label="stickySide.share" />
            </FontIconSpan>
            <SocialLinksContainer type={'navi'} onCopiedToClipboard={this.onCopiedToClipboard} />
          </StickyElement>
        </IfSocialLinksAvailable>

        {showPrintButton && searchLink && (
          <StickyElementLink
            to={{
              pathname: searchLink,
              search: '?print'
            }}
          >
            <FontIconSpanPrint icon="fax_outline">
              <Typo3Label label="stickySide.buttonPrint">
                Druckansicht
              </Typo3Label>
            </FontIconSpanPrint>
          </StickyElementLink>
        )}

        {this.state.showCopyAlertTimeout && (
          <AlertElementContainer>
            <StickyAlertElement>
                <Typo3Label label="stickySideAlert.copy" />
            </StickyAlertElement>
          </AlertElementContainer>
        )}
      </Sticky>
    );
  }
}

export default withRouter(withTypo3(withTypo3Translation(withModal(StickySideElement))));
